import { getInstanceId } from '@va/dashboard/selectors/app';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import {
  CustomDomainPlatformType,
  CustomDomainsApi,
  CustomDomainTarget,
  CustomDomainType,
} from '@va/shared/api/custom-domains';
import { useGetTargetedDNSRecords } from '@va/shared/api/custom-domains/custom-domains.api.hooks';
import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';

type CustomTrackingCodeDomainCreationCtx = ReturnType<typeof useContextValue>;

const CustomTrackingCodeDomainCreationCtx = createContext<CustomTrackingCodeDomainCreationCtx>(
  {} as CustomTrackingCodeDomainCreationCtx,
);

export enum CreationSteps {
  verifyDNS = 'verifyDNS',
  confirmEffects = 'confirmEffects',
}

export type CustomTrackingCodeDomainCreationCtxParams = {
  closeModal: () => void;
  domain: string;
  customDomainId: number | undefined; // if this is provided, we perform activation instead of creation
  onSuccess?: () => void;
};

export const CustomTrackingCodeDomainCreationCtxProvider = ({
  children,
  ...rest
}: PropsWithChildren<CustomTrackingCodeDomainCreationCtxParams>) => {
  const value = useContextValue({ ...rest });
  return (
    <CustomTrackingCodeDomainCreationCtx.Provider value={value}>
      {children}
    </CustomTrackingCodeDomainCreationCtx.Provider>
  );
};

const useContextValue = ({
  domain,
  closeModal,
  onSuccess,
  customDomainId,
}: CustomTrackingCodeDomainCreationCtxParams) => {
  const [activeStep, setActiveStep] = useState<CreationSteps>(CreationSteps.verifyDNS);

  const translate = useTranslate();

  const dnsRecordsReq = useGetTargetedDNSRecords(
    !domain
      ? null
      : {
          platform: CustomDomainPlatformType.STANDALONE,
          domain: domain,
          target: CustomDomainTarget.trackingCode,
        },
  );

  const websiteId = useSelector(getInstanceId);
  const listCustomDomainReq = CustomDomainsApi.useListWebsiteDomainsByType({
    type: CustomDomainType.trackingCode,
    websiteId: websiteId,
  });

  const { showErrorNotification } = useAddNotification();

  const saveCustomDomain = useCallback(async () => {
    try {
      if (customDomainId) {
        await CustomDomainsApi.activateWebsiteDomain({
          websiteId: websiteId,
          customDomainId: customDomainId,
        });
      } else {
        await CustomDomainsApi.createWebsiteDomain({
          websiteId: websiteId,
          domain: domain,
          platform: CustomDomainPlatformType.STANDALONE,
          type: CustomDomainType.trackingCode,
        });
      }

      await listCustomDomainReq.mutate();
      closeModal();
      onSuccess?.();
    } catch (error) {
      console.error(error);
      showErrorNotification(undefined, CustomDomainsApi.extractApiErrorMessage(error, translate));
    }
  }, [closeModal, customDomainId, domain, listCustomDomainReq, onSuccess, showErrorNotification, translate, websiteId]);

  return { activeStep, setActiveStep, dnsRecordsReq, closeModal, domain, saveCustomDomain };
};

export const useCustomTrackingCodeDomainCreationCtx = () => useContext(CustomTrackingCodeDomainCreationCtx);
